import { useState } from "react"
import { Plus, Sparkles, Loader2 } from "lucide-react"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/context/auth/auth-context"
import hive from "@/lib/api/hive"
import { getRandomPortfolioName } from "@/lib/portfolio-utils"
import { Analytics } from "@/lib/analytics"

interface CreatePortfolioDialogProps {
	onSuccess?: () => void
}

export function CreatePortfolioDialog({ onSuccess }: CreatePortfolioDialogProps) {
	const { token } = useAuth()
	const [open, setOpen] = useState(false)
	const [name, setName] = useState("")
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const handleOpenChange = (newOpen: boolean) => {
		if (newOpen) {
			setName(getRandomPortfolioName())
			setError(null)
		}
		setOpen(newOpen)
	}

	const handleRandomizeName = () => {
		setName(getRandomPortfolioName())
	}

	const handleCreate = async () => {
		if (!name.trim()) {
			setError("Please enter a name for your hive")
			return
		}

		if (!token) return

		setIsLoading(true)
		setError(null)

		try {
			await hive.createPortfolio({ name }, token)
			Analytics.capture("Portfolio Created")
			setOpen(false)
			if (onSuccess) onSuccess()
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to create hive")
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				<div className="group cursor-pointer border-2 border-dashed border-sky-200 dark:border-sky-900/50 rounded-xl p-6 flex flex-col items-center justify-center gap-4 hover:border-sky-400 dark:hover:border-sky-700 transition-all bg-sky-50/30 dark:bg-sky-900/5 min-h-[180px]">
					<div className="w-12 h-12 rounded-full bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center group-hover:scale-110 transition-transform">
						<Plus className="h-6 w-6 text-sky-600" />
					</div>
					<div className="text-center">
						<h3 className="font-bold text-sky-900 dark:text-sky-100">Create New Hive</h3>
						<p className="text-xs text-muted-foreground">Start a fresh honeycomb</p>
					</div>
				</div>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Create New Hive</DialogTitle>
					<DialogDescription>
						Give your new portfolio a name. You can add holdings once it's created.
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="space-y-2">
						<Label htmlFor="name">Hive Name</Label>
						<div className="flex gap-2">
							<Input
								id="name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Retirement Hive"
								className="flex-1"
							/>
							<Button
								type="button"
								variant="outline"
								size="icon"
								onClick={handleRandomizeName}
								title="Generate random name"
							>
								<Sparkles className="h-4 w-4" />
							</Button>
						</div>
						{error && <p className="text-sm text-red-500 font-medium">{error}</p>}
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
						Cancel
					</Button>
					<Button
						onClick={handleCreate}
						className="bg-sky-600 hover:bg-sky-700 text-white"
						disabled={isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Creating...
							</>
						) : (
							"Create Hive"
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
